import { Checkbox } from "../../components/ui/checkbox";
import { CreateCourseDialog } from "./CreateCourseDialog";
import { useState } from "react";
import {
  BookOpen,
  CheckCircle2,
  ExternalLink,
  Plus,
  Send,
  UserPlus,
} from "lucide-react";
import { useUserDirectory } from "../../backend/queries/users";
import {
  useAddQuestion,
  useAssignCourse,
  useCompleteCourse,
  useCourse,
  usePublishCourse,
  useSubmitQuiz,
} from "../../backend/queries/learning";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageSection,
} from "../../components/patterns";

export function CoursePanel({
  course,
  admin,
  completed = false,
  preview = false,
}: {
  course: {
    id: string;
    title: string;
    description: string;
    pass_percent: number;
    status: string;
    content_url?: string | null;
    content_urls?: string[];
  };
  admin: boolean;
  completed?: boolean;
  preview?: boolean;
}): JSX.Element {
  const detail = useCourse(course.id);
  const publish = usePublishCourse();
  const submitQuiz = useSubmitQuiz();
  const complete = useCompleteCourse();
  const [confirmed, setConfirmed] = useState(false);
  const finished = completed || complete.isSuccess;
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [result, setResult] = useState<{
    score: number;
    passed: boolean;
  } | null>(null);
  const [error, setError] = useState("");
  const submit = async () => {
    const questions = detail.data?.questions || [];
    if (Object.keys(answers).length !== questions.length) {
      setError("Answer every question before submitting.");
      return;
    }
    setError("");
    try {
      const value = (await submitQuiz.mutateAsync({
        id: course.id,
        payload: {
          answers: questions.map((q) => ({
            question_id: q.id,
            selected_index: answers[q.id],
          })),
        },
      })) as { score: number; passed: boolean };
      setResult(value);
    } catch {
      setError("Your quiz could not be submitted. Try again.");
    }
  };
  return (
    <PageSection
      title={course.title}
      description={
        course.description ||
        (course.content_url
          ? undefined
          : `${course.pass_percent}% required to pass`)
      }
      actions={
        admin ? (
          <div className="flex flex-wrap gap-2">
            <CreateCourseDialog course={course} />
            {course.status === "draft" && !course.content_url && (
              <AddQuestionDialog
                courseId={course.id}
                nextPosition={(detail.data?.questions.length || 0) + 1}
              />
            )}
            {course.status === "draft" && (
              <Button
                onClick={() => publish.mutate(course.id)}
                disabled={
                  (!course.content_url && !detail.data?.questions.length) ||
                  publish.isPending
                }
              >
                <Send className="h-4 w-4" />
                Publish
              </Button>
            )}
            {course.status === "published" && (
              <AssignCourseDialog courseId={course.id} />
            )}
          </div>
        ) : undefined
      }
    >
      {course.content_url ? (
        <div className="grid gap-5">
          <div className="grid gap-3">
            <p className="text-sm font-medium">
              {admin ? "Resources" : "1. Complete the resources"}
            </p>
            {!admin && (
              <p className="text-sm text-muted-foreground">
                Links open in a new tab. Return here when you’re finished.
              </p>
            )}
            <ol className="divide-y rounded-md border">
              {(course.content_urls?.length
                ? course.content_urls
                : [course.content_url]
              ).map((url, index) => (
                <li
                  key={`${index}-${url}`}
                  className="flex items-center justify-between gap-3 p-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium">Resource {index + 1}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {url}
                    </p>
                  </div>
                  <Button asChild variant="outline" size="sm">
                    <a
                      href={url || undefined}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`Open resource ${index + 1} in a new tab`}
                    >
                      Open
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                </li>
              ))}
            </ol>
          </div>
          {!admin &&
            (finished ? (
              <p
                role="status"
                className="flex items-center gap-2 text-sm font-medium"
              >
                <CheckCircle2 className="h-4 w-4" />
                Completed — your progress is saved.
              </p>
            ) : (
              <div className="grid gap-3">
                <p className="text-sm font-medium">2. Confirm completion</p>
                <label className="flex items-center gap-3 text-sm">
                  <Checkbox
                    checked={confirmed}
                    onCheckedChange={(value) => setConfirmed(value === true)}
                    disabled={complete.isPending}
                  />
                  I’ve completed all the resources above.
                </label>
                <Button
                  className="w-fit"
                  onClick={() => complete.mutate(course.id)}
                  disabled={preview || !confirmed || complete.isPending}
                >
                  {complete.isPending ? "Saving…" : "Complete course"}
                </Button>
              </div>
            ))}
          {complete.error && (
            <Alert variant="destructive">
              <AlertDescription>{complete.error.message}</AlertDescription>
            </Alert>
          )}
        </div>
      ) : !admin && completed ? (
        <p role="status" className="text-sm font-medium">
          Completed — you’ve passed this quiz.
        </p>
      ) : detail.isPending ? (
        <LoadingState label="Loading course" />
      ) : detail.error ? (
        <ErrorState onRetry={() => detail.refetch()} />
      ) : (
        <div className="grid gap-4">
          {!admin && (
            <p className="text-sm text-muted-foreground">
              Answer every question, then submit. You need {course.pass_percent}
              % to pass and can retry.
            </p>
          )}
          {detail.data?.questions.map((question, index) => (
            <Card key={question.id}>
              <CardHeader>
                <CardTitle className="text-base">
                  {index + 1}. {question.prompt}
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-2">
                {question.options.map((option, optionIndex) =>
                  admin ? (
                    <div
                      key={optionIndex}
                      className={`rounded-md border px-3 py-2 text-sm ${question.correct_index === optionIndex ? "border-tone-success-border bg-tone-success-surface" : ""}`}
                    >
                      {option}
                      {question.correct_index === optionIndex && (
                        <CheckCircle2 className="ml-2 inline h-4 w-4" />
                      )}
                    </div>
                  ) : (
                    <label
                      key={optionIndex}
                      className="flex cursor-pointer items-center gap-3 rounded-md border px-3 py-3 hover:bg-muted"
                    >
                      <input
                        type="radio"
                        name={question.id}
                        checked={answers[question.id] === optionIndex}
                        onChange={() =>
                          setAnswers((current) => ({
                            ...current,
                            [question.id]: optionIndex,
                          }))
                        }
                      />
                      <span className="text-sm">{option}</span>
                    </label>
                  ),
                )}
              </CardContent>
            </Card>
          ))}
          {!detail.data?.questions.length && (
            <EmptyState
              title="No questions yet"
              description="Add at least one question before publishing."
            />
          )}
          {!admin && detail.data?.questions.length ? (
            <div className="flex flex-col items-start gap-3">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              {result && (
                <Alert>
                  <BookOpen className="h-4 w-4" />
                  <AlertDescription>
                    {result.passed
                      ? `Passed with ${result.score}%.`
                      : `Score: ${result.score}%. Review the material and try again.`}
                  </AlertDescription>
                </Alert>
              )}
              <Button
                onClick={submit}
                disabled={preview || submitQuiz.isPending}
              >
                {submitQuiz.isPending ? "Submitting…" : "Submit quiz"}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </PageSection>
  );
}

function AddQuestionDialog({
  courseId,
  nextPosition,
}: {
  courseId: string;
  nextPosition: number;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [correct, setCorrect] = useState("0");
  const add = useAddQuestion();
  const submit = async () => {
    await add.mutateAsync({
      id: courseId,
      payload: {
        prompt,
        options,
        correct_index: Number(correct),
        position: nextPosition,
      },
    });
    setOpen(false);
    setPrompt("");
    setOptions(["", ""]);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Plus className="h-4 w-4" />
          Question
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add quiz question</DialogTitle>
          <DialogDescription>
            Learners will not receive the correct answer in the course response.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <Label htmlFor="question-prompt">Question</Label>
          <Textarea
            id="question-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
          {options.map((option, index) => (
            <div className="grid gap-1" key={index}>
              <Label htmlFor={`option-${index}`}>Option {index + 1}</Label>
              <Input
                id={`option-${index}`}
                value={option}
                onChange={(e) =>
                  setOptions((current) =>
                    current.map((v, i) => (i === index ? e.target.value : v)),
                  )
                }
              />
            </div>
          ))}
          <Button
            variant="ghost"
            onClick={() => setOptions((current) => [...current, ""])}
            disabled={options.length >= 8}
          >
            Add option
          </Button>
          <Label>Correct option</Label>
          <Select value={correct} onValueChange={setCorrect}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map((_, i) => (
                <SelectItem key={i} value={String(i)}>
                  Option {i + 1}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={
              add.isPending || !prompt.trim() || options.some((v) => !v.trim())
            }
          >
            Add question
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignCourseDialog({ courseId }: { courseId: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [agent, setAgent] = useState("");
  const [search, setSearch] = useState("");
  const [required, setRequired] = useState(false);
  const agents = useUserDirectory({ status: "active", search, limit: 50 });
  const assign = useAssignCourse();
  const submit = async () => {
    try {
      await assign.mutateAsync({ id: courseId, userId: agent, required });
      setOpen(false);
      setAgent("");
      setRequired(false);
    } catch {
      /* The mutation error is displayed below. */
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <UserPlus className="h-4 w-4" />
          Assign
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign course</DialogTitle>
          <DialogDescription>
            Assign learning to an admin or agent.
          </DialogDescription>
        </DialogHeader>
        <Input
          aria-label="Find user"
          placeholder="Find a user by name or email"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Select value={agent} onValueChange={setAgent}>
          <SelectTrigger>
            <SelectValue placeholder="Select a user" />
          </SelectTrigger>
          <SelectContent>
            {agents.data?.items.filter((item) => (item.role === "Owner" || item.role === "Admin") || item.role === "Agent").map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.firstname} {item.lastname}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={required}
            onChange={(event) => setRequired(event.target.checked)}
          />
          Required before using the portal
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          {assign.error && (
            <p role="alert" className="text-sm text-destructive">
              {assign.error.message}
            </p>
          )}
          <Button onClick={submit} disabled={!agent || assign.isPending}>
            Assign course
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
